import type {ReactNode} from 'react';
import {CUSTOM_SLIDESHOW_CONTROLS_WRAPPER_LEFT_OFFSET, CUSTOM_SLIDESHOW_CONTROLS_WRAPPER_TOP_OFFSET} from './custom-slideshow-controls-wrapper.types';
import styles from './custom-slideshow-controls-wrapper.module.scss';
import type {CustomItemControlPositions} from '@Components/poster-editor/components/custom-item-controls/components/single-item-custom-controls/single-item-custom-controls.types';
import {degreesToRadians} from '@Utils/math.util';
import {CUSTOM_ITEM_CONTROL_WITH_ICON_SIDE} from '@Components/poster-editor/components/custom-item-controls/custom-item-controls.types';
import {BaseCustomItemControl} from '@Components/poster-editor/components/custom-item-controls/components/base-custom-item-control/base-custom-item-control';
import {useAppSelector} from '@/hooks';
import {ControlType} from '@Components/poster-editor/components/custom-item-controls/components/base-custom-item-control/base-custom-item-control.types';
import type {CustomItemControlsProps} from '@Components/poster-editor/components/custom-item-controls/components/custom-item-controls-wrapper/custom-item-controls-wrapper.types';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';

const CUSTOM_PREVIOUS_SLIDE_CONTROL_ID = 'custom-previous-slide';
const CUSTOM_NEXT_SLIDE_CONTROL_ID = 'custom-next-slide';

export function CustomSlideshowControlsWrapper(props: CustomItemControlsProps): ReactNode | null {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeSlideUID = useGetSelectedSlideId();

  const arePosterItemsMoving = useAppSelector((state) => {
    return state.posterEditor.arePosterItemsMoving[props.pageHashedId];
  });
  const arePosterItemsScaling = useAppSelector((state) => {
    return state.posterEditor.arePosterItemsScaling[props.pageHashedId];
  });
  const arePosterItemsRotating = useAppSelector((state) => {
    return state.posterEditor.arePosterItemsRotating[props.pageHashedId];
  });

  if (!activeItemId || !activeItem || !activeSlideUID || !activeItem.isSlideshow()) {
    return null;
  }

  const getSlideshowControlsWrapperPosition = (): CustomItemControlPositions => {
    const leftInitial =
      (props.positionProperties.left - props.positionProperties.scaledHeight * Math.sin(degreesToRadians(props.positionProperties.angle))) * props.positionProperties.posterScale;
    const topInitial =
      (props.positionProperties.top + props.positionProperties.scaledHeight * Math.cos(degreesToRadians(props.positionProperties.angle))) * props.positionProperties.posterScale;

    const left =
      leftInitial -
      CUSTOM_SLIDESHOW_CONTROLS_WRAPPER_LEFT_OFFSET * Math.cos(degreesToRadians(props.positionProperties.angle)) -
      CUSTOM_SLIDESHOW_CONTROLS_WRAPPER_TOP_OFFSET * Math.sin(degreesToRadians(props.positionProperties.angle));
    const top =
      topInitial +
      CUSTOM_SLIDESHOW_CONTROLS_WRAPPER_TOP_OFFSET * Math.cos(degreesToRadians(props.positionProperties.angle)) -
      CUSTOM_SLIDESHOW_CONTROLS_WRAPPER_LEFT_OFFSET * Math.sin(degreesToRadians(props.positionProperties.angle));

    return {left, top, angle: props.positionProperties.angle};
  };

  const getSlideshowControlsWeapper = (): ReactNode | null => {
    const position = getSlideshowControlsWrapperPosition();

    return (
      <div
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
          transformOrigin: `0 0`,
          rotate: `${position.angle}deg`,
        }}
        className={`flexbox ${styles.slideShowControlWrapper} _position-absolute`}
      >
        <BaseCustomItemControl
          isDisabled={!activeItem.slides.hasPreviousSlide()}
          icon={'icon-slide-prev'}
          controlId={CUSTOM_PREVIOUS_SLIDE_CONTROL_ID}
          top={0}
          left={0}
          width={CUSTOM_ITEM_CONTROL_WITH_ICON_SIDE}
          height={CUSTOM_ITEM_CONTROL_WITH_ICON_SIDE}
          angle={0}
          borderRadius={'50%'}
          isVisible={!arePosterItemsMoving && !arePosterItemsRotating && !arePosterItemsScaling}
          onPointerDown={() => {
            activeItem.selectPreviousSlide();
          }}
          controlType={ControlType.CLICK}
        />
        <BaseCustomItemControl
          isDisabled={!activeItem.slides.hasNextSlide()}
          icon={'icon-slide-next'}
          controlId={CUSTOM_NEXT_SLIDE_CONTROL_ID}
          top={0}
          left={35}
          width={CUSTOM_ITEM_CONTROL_WITH_ICON_SIDE}
          height={CUSTOM_ITEM_CONTROL_WITH_ICON_SIDE}
          angle={0}
          borderRadius={'50%'}
          isVisible={!arePosterItemsMoving && !arePosterItemsRotating && !arePosterItemsScaling}
          onPointerDown={() => {
            activeItem.selectNextSlide();
          }}
          controlType={ControlType.CLICK}
        />
      </div>
    );
  };

  if (arePosterItemsMoving || arePosterItemsRotating || arePosterItemsScaling) {
    return null;
  }

  return getSlideshowControlsWeapper();
}
